import { Box, Button, Typography } from "@mui/material";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

interface UpdateNotificationProps {
  isVisible: boolean;
  isUpdating?: boolean;
  onAccept: () => void;
  onDismiss: () => void;
}

/**
 * "New version ready" toast.
 *
 * Rendered through a portal into document.body for the same reason ArtistNav
 * is: an ancestor inside the app shell creates a containing block, so a
 * position:fixed toast left inside the tree anchors to that box instead of the
 * viewport and lands off to the right. Centring also has to come from the box
 * model, never a transform, because framer-motion writes its own transform for
 * the entrance and would wipe a translateX(-50%).
 *
 * Docked bottom-centre, clearing the fixed Fan Hub bar (62px plus its 0.5rem
 * inset on mobile, 4.1rem plus 1.25rem from 900px up). z-index 10010 sits
 * above the Fan Hub bar and below the modal layer at 10020, so an open drawer
 * still covers it.
 */
export function UpdateNotification({
  isVisible,
  isUpdating = false,
  onAccept,
  onDismiss,
}: UpdateNotificationProps) {
  const prefersReducedMotion = useReducedMotion();
  const [host, setHost] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setHost(document.body);
  }, []);

  if (!host) return null;

  return createPortal(
    <AnimatePresence>
      {isVisible && (
        <Box
          component={motion.aside}
          initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: prefersReducedMotion ? 0 : 10 }}
          transition={{ duration: prefersReducedMotion ? 0 : 0.22, ease: [0.22, 1, 0.36, 1] }}
          role="status"
          aria-live="polite"
          sx={{
            position: "fixed",
            zIndex: 10010,
            left: "0.5rem",
            right: "0.5rem",
            marginInline: "auto",
            bottom: {
              xs: "calc(62px + 0.5rem + 10px + env(safe-area-inset-bottom, 0px))",
              md: "calc(4.1rem + 1.25rem + 0.75rem + env(safe-area-inset-bottom, 0px))",
            },
            width: "auto",
            maxWidth: { xs: "none", md: "27rem" },
            display: "flex",
            alignItems: "center",
            gap: "14px",
            flexWrap: "wrap",
            padding: "14px 16px",
            borderRadius: "14px",
            border: "1px solid color-mix(in srgb, var(--logoColor, #d9c08a) 24%, transparent)",
            backgroundColor: "var(--color5, #141414)",
            boxShadow: "0 12px 32px rgba(0, 0, 0, 0.45)",
          }}
        >
          <Box sx={{ flex: "1 1 12rem", minWidth: 0 }}>
            <Typography
              component="p"
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontWeight: 600,
                color: "var(--logoColor, #d9c08a)",
                fontSize: { xs: "16px", md: "15px" },
                lineHeight: 1.3,
                margin: 0,
              }}
            >
              New version ready
            </Typography>
            <Typography
              component="p"
              sx={{
                fontFamily: "Poppins, sans-serif",
                color: "var(--textColor, #e8e8e8)",
                fontSize: { xs: "14px", md: "13px" },
                lineHeight: 1.5,
                letterSpacing: 0,
                margin: "3px 0 0",
              }}
            >
              Reload to get the latest Truevined.
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
            <Button
              type="button"
              onClick={onAccept}
              disabled={isUpdating}
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontSize: { xs: "15px", md: "14px" },
                fontWeight: 600,
                textTransform: "none",
                borderRadius: "999px",
                padding: "8px 20px",
                minHeight: "40px",
                color: "var(--backgroundColor, #0b0b0b)",
                backgroundColor: "var(--logoColor, #d9c08a)",
                "&:hover": { backgroundColor: "var(--logoColor2, #f0dfb6)" },
                "&:focus-visible": {
                  outline: "2px solid var(--logoColor2, #f0dfb6)",
                  outlineOffset: "2px",
                },
                "&.Mui-disabled": { opacity: 0.7, color: "var(--backgroundColor, #0b0b0b)" },
              }}
            >
              {isUpdating ? "Reloading" : "Reload"}
            </Button>
            <Button
              type="button"
              onClick={onDismiss}
              disabled={isUpdating}
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontSize: { xs: "15px", md: "14px" },
                fontWeight: 500,
                textTransform: "none",
                borderRadius: "999px",
                padding: "8px 16px",
                minHeight: "40px",
                color: "var(--textColor, #e8e8e8)",
                "&:hover": {
                  backgroundColor: "color-mix(in srgb, var(--logoColor, #d9c08a) 12%, transparent)",
                },
                "&:focus-visible": {
                  outline: "2px solid var(--logoColor2, #f0dfb6)",
                  outlineOffset: "2px",
                },
              }}
            >
              Later
            </Button>
          </Box>
        </Box>
      )}
    </AnimatePresence>,
    host,
  );
}
